import React from 'react';
import {Box, ButtonBase, Divider, LinearProgress} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import {Typography} from "@mui/joy";
import {useTranslation} from "react-i18next";
import {FormProgress} from "../../utils/contaminationDeclaration/gating";
import {sectionStatusColor} from "./statusColors";
import PrintDeclarationButton from "../ContaminationDeclarationStatus/PrintDeclarationButton";

interface Props {
    progress: FormProgress;
    onJump: (sectionKey: string) => void;
    declarationId: string;
    /** Printable *and* nothing unsaved — the same verdict the footer shows. */
    ready: boolean;
    printBlockedReason: string;
}

/**
 * Sidebar panel next to the editor (wide screens only): overall progress, one
 * row per section with its live open count (rows jump to their section), and
 * the print action above the notes. Mirrors the prototype's status column.
 */
const FormStatusPanel = ({progress, onJump, declarationId, ready, printBlockedReason}: Props) => {
    const {t} = useTranslation();
    const theme = useTheme();

    return (
        <Box data-testid="doc-status-panel" sx={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
            <Typography level="titleSmall" sx={{color: '#333'}}>{t('Progress')}</Typography>
            <LinearProgress
                variant="determinate"
                value={progress.percent}
                sx={{height: 6, borderRadius: 3, backgroundColor: '#e0e0e0'}}
            />
            <Typography level="body-xs" sx={{color: 'text.tertiary'}}>
                {t('{{percent}}% complete · {{count}} open', {percent: progress.percent, count: progress.totalOpen})}
            </Typography>

            <Box sx={{display: 'flex', flexDirection: 'column'}}>
                {progress.sections.map((section) => (
                    <ButtonBase
                        key={section.key}
                        onClick={() => onJump(section.key)}
                        data-testid={`doc-status-panel-${section.key}`}
                        sx={{
                            display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                            justifyContent: 'flex-start', textAlign: 'left',
                            py: '6px', px: '4px', borderRadius: '4px',
                            '&:hover': {backgroundColor: 'rgba(0, 0, 0, 0.04)'},
                        }}
                    >
                        <Box sx={{width: 10, height: 10, borderRadius: '50%', flexShrink: 0, bgcolor: sectionStatusColor(theme, section.status)}}/>
                        <Typography level="bodySmallBold" sx={{flex: 1, minWidth: 0, color: '#333'}} noWrap>
                            {`${section.number} · ${section.title}`}
                        </Typography>
                        <Typography level="body-xs" sx={{color: 'text.tertiary', flexShrink: 0}}>
                            {section.status === 'inactive'
                                ? t('Not applicable')
                                : section.openIndices.length > 0
                                    ? t('{{count}} open', {count: section.openIndices.length})
                                    : t('Complete')}
                        </Typography>
                    </ButtonBase>
                ))}
            </Box>

            <Divider/>
            <PrintDeclarationButton
                declarationId={declarationId}
                disabled={!ready}
                tooltip={ready ? undefined : printBlockedReason}
                size="medium"
                fullWidth
                data-testid="doc-print-panel"
            />
            <Typography level="body-xs" sx={{color: 'text.tertiary'}}>
                {t('"No" is an answer – blank is not. Every active question must be answered before the form can be printed.')}
            </Typography>
            <Typography level="body-xs" sx={{color: 'text.tertiary'}}>
                {t('Save anytime – there is no finalize step. The last editor is named on the printed form.')}
            </Typography>
        </Box>
    );
};

export default FormStatusPanel;
